import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, Sort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { DbCraft } from 'src/app/shared/models/craft.model';
import { TextDividerComponent } from 'src/app/shared/components/text-divider/text-divider.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-crafts',
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSortModule,
    TextDividerComponent,
    MatButtonModule,
    MatDialogModule,
    MatDividerModule,
    MatCardModule,
    SmallNumberPipe,
  ],
  templateUrl: './crafts.component.html',
  styleUrls: ['./crafts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CraftsComponent implements OnInit, OnDestroy, AfterViewInit {
  displayedColumns: string[] = ['productUniqueName', 'bought', 'sold', 'amountCrafted', 'investment', 'result', 'resultPerFocus', 'date', 'delete'];
  sortedData: DbCraft[] = [];
  userCraftsTotal: {
    count: number;
    totalIncome: number;
    totalCost: number;
    totalProfit: number;
  } = {
    count: 0,
    totalIncome: 0,
    totalCost: 0,
    totalProfit: 0,
  };
  subscriptions: Subscription[] = [];

  constructor(
    public authService: AuthService,
    public userSettingsService: UserSettingsService,
    public jsonReaderService: JsonReaderService,
    private matDialog: MatDialog,
    private smallNumberPipe: SmallNumberPipe,
    private cdr: ChangeDetectorRef
  ) {
    jsonReaderService.ensureLoaded();

    userSettingsService.ensureUserCraftsLoaded();

    this.subscriptions.push(
      userSettingsService.userCrafts$.subscribe((crafts) => {
        this.sortedData = crafts.sort((a, b) => b.date.getTime() - a.date.getTime());
        this.cdr.markForCheck();
      }),
      userSettingsService.userCraftTotals$.subscribe((totals) => {
        this.userCraftsTotal = totals;
        this.cdr.markForCheck();
      }),
      userSettingsService.afmUser$.subscribe((user) => {
        userSettingsService.ensureUserCraftsLoaded();
      })
    );
  }

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {}

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngAfterViewInit() {}

  async deleteCraft(craft: DbCraft): Promise<void> {
    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Deletion', message: `Are you sure you want to delete this craft?` },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.userSettingsService.deleteUserCraft(craft);
        this.cdr.markForCheck();
      }
    });
  }

  sortData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      return;
    }

    this.sortedData = [...this.sortedData].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'productUniqueName':
          return this.compare(
            this.jsonReaderService.getItemFromIndex(a.productUniqueName)?.localizedName ?? '',
            this.jsonReaderService.getItemFromIndex(b.productUniqueName)?.localizedName ?? '',
            isAsc
          );
        case 'amountCrafted':
          return this.compare(a.amountCrafted, b.amountCrafted, isAsc);
        case 'result':
          return this.compare(a.result, b.result, isAsc);
        case 'resultPerFocus':
          return this.compare(a.resultPerFocus, b.resultPerFocus, isAsc);
        case 'date':
          return this.compare(a.date.getTime(), b.date.getTime(), isAsc);
        default:
          return 0;
      }
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
